import { X, Check } from "lucide-react";
import useReveal from "@/hooks/useReveal";

const PAIN_POINTS = [
  "Hours spent calculating complex working interests",
  "Mailing out manual JIB statements",
  "Sweating over revenue split deadlines",
];

const WIN_POINTS = [
  "Set your ownership percentages once",
  "Expenses are instantly tracked, JIBs auto-generated",
  "Payouts move securely with a click",
];

export default function FrictionSection() {
  const headerRef = useReveal();
  const leftRef = useReveal();
  const rightRef = useReveal({ threshold: 0.15 });

  return (
    <section
      id="friction"
      data-testid="friction-section"
      className="relative bg-[#F4F3EE] py-24 md:py-32"
    >
      <div className="gp-container">
        <div ref={headerRef} className="reveal max-w-3xl mb-16 md:mb-20">
          <span className="gp-overline">// The friction correction</span>
          <h2
            data-testid="friction-heading"
            className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-[#111418] leading-[1.05]"
          >
            Why change how you{" "}
            <span className="italic text-[#0F4C5C]">manage your wells?</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-10">
          {/* Before */}
          <article
            ref={leftRef}
            data-testid="friction-before"
            className="reveal relative bg-[#EBE9E1] border border-[#D3D9DE] p-8 lg:p-10"
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="inline-flex h-9 w-9 items-center justify-center bg-[#7A3906] text-white">
                <X size={18} strokeWidth={2.4} />
              </span>
              <span className="gp-overline" style={{ color: "#7A3906" }}>
                // Before
              </span>
            </div>
            <h3 className="font-display text-2xl md:text-3xl text-[#111418] leading-tight">
              The Manual Hassle
            </h3>
            <ul className="mt-6 space-y-4">
              {PAIN_POINTS.map((p) => (
                <li key={p} className="flex items-start gap-3">
                  <span
                    aria-hidden
                    className="mt-2 inline-block h-1.5 w-3 bg-[#7A3906] opacity-70"
                  />
                  <span className="text-[15px] text-[#2C333A] leading-relaxed line-through decoration-[#7A3906]/40">
                    {p}
                  </span>
                </li>
              ))}
            </ul>
            <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.22em] text-[#7A3906]">
              · Hours per month, lost
            </p>
          </article>

          {/* After */}
          <article
            ref={rightRef}
            data-testid="friction-after"
            className="reveal relative bg-[#082933] text-[#F4F3EE] p-8 lg:p-10 shadow-[12px_12px_0_0_#D67229]"
          >
            <div className="gp-grain" aria-hidden />
            <div className="relative">
              <div className="flex items-center gap-3 mb-6">
                <span className="inline-flex h-9 w-9 items-center justify-center bg-[#D67229] text-white">
                  <Check size={18} strokeWidth={2.6} />
                </span>
                <span className="gp-overline" style={{ color: "#E88B46" }}>
                  // The GushPay edge
                </span>
              </div>
              <h3 className="font-display text-2xl md:text-3xl text-[#F4F3EE] leading-tight">
                Set It. Run It. Done.
              </h3>
              <ul className="mt-6 space-y-4">
                {WIN_POINTS.map((p) => (
                  <li key={p} className="flex items-start gap-3">
                    <span
                      aria-hidden
                      className="mt-1 inline-flex h-4 w-4 items-center justify-center bg-[#E88B46] text-[#082933] flex-shrink-0"
                    >
                      <Check size={11} strokeWidth={3} />
                    </span>
                    <span className="text-[15px] text-[#F4F3EE] leading-relaxed">
                      {p}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.22em] text-[#E88B46]">
                · Set once. Run forever.
              </p>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
}
